<template>
  <main-container class="model-predict-result-list-container" title="モデル適用結果の評価一覧" :subTitle="modelName">
    <template slot="main-content">
      <div style="flex-grow: 1; height: 100%;">
        <h3 v-if="predictEvaluations != null && predictEvaluations.length <= 0">評価結果がありません</h3>
        <h3 v-if="predictEvaluations != null && predictEvaluations.length > 0">全件数：{{predictEvaluations.length |
          number_format}}</h3>
        <fixed-header-table v-if="predictEvaluations != null && predictEvaluations.length > 0" :fixed="true">
          <tr slot="header">
            <th>キー
              <el-popover placement="bottom-start">
                <el-autocomplete :fetch-suggestions="fetchSuggestionsKey" placeholder="IDまたはアップロードファイル名"
                                 v-model="filterKey" clearable="true" style="width: 400px;"
                                 @change="onChangeFilter"></el-autocomplete>
                <font-awesome-icon slot="reference" class="icon filter" icon="filter" style="margin-left: 16px;"
                                   :class="{ 'has-filter': filterKey }"/>
              </el-popover>
            </th>
            <th>評価日時
              <el-popover placement="bottom-start">
                <div style="display: flex; flex-direction: row; align-items: center;">
                  <el-date-picker v-model="filterFrom" type="date" placeholder="開始日" format="yyyy/MM/dd"
                                  value-format="yyyyMMdd" @change="onChangeFilter"></el-date-picker>
                  <span style="margin: 0 16px;">～</span>
                  <el-date-picker v-model="filterTo" type="date" placeholder="終了日" format="yyyy/MM/dd"
                                  value-format="yyyyMMdd" @change="onChangeFilter"></el-date-picker>
                </div>
                <font-awesome-icon slot="reference" class="icon filter" icon="filter" style="margin-left: 16px;"
                                   :class="{ 'has-filter': (filterFrom || filterTo) }"/>
              </el-popover>
            </th>
            <th>データ件数</th>
            <th v-for="evaluation in modelEvaluations" style="min-width: 160px; flex:0;">{{evaluation.nameJa ||
              evaluation.name}}
              <tooltip-icon class="evaluation-tip" v-if="evaluation.description && evaluation.description.text"
                            :content="evaluation.description.text"></tooltip-icon>
            </th>
            <th style="flex-grow: 0; min-width: 166px; max-width: 166px;"></th>
          </tr>
          <tr v-for="(result, index) in filteredResults()">
            <td>{{result.key}}</td>
            <td>{{result.updatedAt | date_format('YYYYMMDDHHmmss', 'YYYY/MM/DD HH:mm')}}</td>
            <td>
              <template v-if="result.statusCode === 0">{{result.count | number_format}}</template>
              <template v-else-if="result.statusCode === 4">評価中</template>
              <template v-else>評価に失敗しました</template>
            </td>
            <td v-for="evaluation in modelEvaluations" style="min-width: 160px; flex:0;">
              <span v-if="result.statusCode === 0 && predictEvaluations[index].evaluations && predictEvaluations[index].evaluations.hasOwnProperty(evaluation.name)">{{Number(predictEvaluations[index].evaluations[evaluation.name]).toFixed(4)}}</span>
              <span v-else>-</span>
            </td>
            <td style="flex-grow: 0; justify-content: center; min-width: 166px; max-width: 166px;">
              <el-button v-if="result.statusCode === -4" style="flex-grow: 1;" type="danger"
                         @click="onConfirmedError(result)">確認
              </el-button>
              <template v-else>-</template>
            </td>
          </tr>
        </fixed-header-table>
      </div>
    </template>

    <template slot="main-footer">
      <el-button class="large" type="default" @click="onClickBack">戻る</el-button>
    </template>
  </main-container>
</template>

<script lang="js">
import BaseView from '../../base'
import PredictResultDialogCmp from '../../../components/model/predict/dialogs/predict-result.vue'
import WebApiClient from '../../../api/web'
import { ModelDetailInfo, PredictEvaluationsResult } from '../../../defines/model/model'
import { Code } from '../../../api/base'
import CommonUtil from '../../../utils/common'
import DateUtil from '../../../utils/date'
import { ModelStatus } from '../../../defines/enum'
import MessageBox from '../../../utils/message-box'
import { Category } from '../../../defines/const'
import AlgorithmUtil from '../../../utils/algorithm'

export default {
  name: 'ModelPredictEvaluationsResultListView',
  mixins: [BaseView],
  components: {
    'predict-result-dialog': PredictResultDialogCmp

  },
  props: {
    modelId: '',
    modelName: ''
  },
  data () {
    return {
      modelInfo: ModelDetailInfo,
      predictEvaluationsResult: PredictEvaluationsResult,
      modelEvaluations: [],
      predictEvaluations: [],

      keyList: [],

      filterKey: '',
      filterFrom: '',
      filterTo: ''
    }
  },
  created () {
    if (CommonUtil.isEmpty(this.modelName)) {
      WebApiClient.getModel(this, this.modelId).then(response => {
        if (response.data.code === Code.SUCCESS) {
          this.modelInfo = response.data.result
          this.modelName = this.modelInfo.modelName
          const purpose = AlgorithmUtil.algorithm2Purpose(this.modelInfo.categoryId,
            this.modelInfo.algorithm.algorithmId)
          if (purpose !== Category.REG && ((Category.CLF === purpose) &&
              (CommonUtil.isEmpty(this.modelInfo.yClasses) || this.modelInfo.yClasses.length > 2))) {
            MessageBox.alert(this, 'このモデルは適用結果の評価ができません。').then(() => {
              this.changeLocation('model-list', null, false, true)
            }).catch(() => {})
            return
          }
          this.$forceUpdate()
          this.loadModelPredictEvaluations()
        }
      })
    } else {
      this.loadModelPredictEvaluations()
    }
  },
  methods: {
    fetchSuggestionsKey (queryString, callback) {
      const results = CommonUtil.isNotEmpty(queryString) ? this.keyList.filter(
        key => key.value.toLowerCase().indexOf(queryString.toLowerCase()) >= 0) : this.keyList
      callback(results)
    },
    loadModelPredictEvaluations () {
      WebApiClient.getModelPredictEvaluations(this, this.modelId).then((response) => {
        this.predictEvaluationsResult = response.data.result
        this.modelEvaluations = this.predictEvaluationsResult.modelEvaluations
        this.predictEvaluations = this.predictEvaluationsResult.predictEvaluations

        const existKeys = []
        for (const predictEvaluations of this.predictEvaluations) {
          if (existKeys.indexOf(predictEvaluations.key) >= 0) continue
          existKeys.push(predictEvaluations.key)
          this.keyList.push({
            value: predictEvaluations.key
          })
        }
        this.$forceUpdate()
      })
    },
    filteredResults () {
      if (CommonUtil.isEmpty(this.filterKey.concat(this.filterFrom).concat(this.filterTo))) {
        return this.predictEvaluations
      }

      const filterFrom = CommonUtil.isNotEmpty(this.filterFrom) ? DateUtil.parse(this.filterFrom, 'YYYYMMDD') : null
      const filterTo = CommonUtil.isNotEmpty(this.filterTo) ? DateUtil.parse(this.filterTo, 'YYYYMMDD') : null

      return this.predictEvaluations.filter(predictResult => {
        if (predictResult.statusCode !== ModelStatus.Normal) return true

        const predictDate = DateUtil.parse(predictResult.updatedAt, 'YYYYMMDD')

        if (CommonUtil.isNotEmpty(this.filterKey)) {
          if (predictResult.key.toLowerCase().indexOf(this.filterKey.toLowerCase()) < 0) return false
        }
        if (CommonUtil.isNotNull(filterFrom)) {
          if (!filterFrom.isSameOrBefore(predictDate)) return false
        }
        if (CommonUtil.isNotNull(filterTo)) {
          if (!filterTo.isSameOrAfter(predictDate)) return false
        }

        return true
      })
    },
    onChangeFilter () {
      this.$forceUpdate()
    },
    onClickBack () {
      this.changeLocation('model-predict-evaluation', {
        modelId: this.modelId,
        modelName: this.modelName
      }, false, true)
    },
    onConfirmedError (predictEvaluation) {
      MessageBox.confirm(this, `適用結果を評価出来ませんでした。<br>データは削除されます。<br>原因不明の場合はお問合せください。`, {
        dangerouslyUseHTMLString: true
      }).then(() => {
        WebApiClient.deleteTask(this, predictEvaluation.predictEvaluationId).then(() => {
          this.predictEvaluations.splice(this.predictEvaluations.indexOf(predictEvaluation), 1)
          this.$forceUpdate()
        })
      }).catch(() => {})
    }
  }
}

</script>

<style scoped>
  .model-predict-result-list-container >>> .main-content {
    display: flex;
    flex-direction: column;
    padding: 24px;
  }

  .icon.filter {
    cursor: pointer;
  }

  .icon.filter:hover {
    opacity: 0.7;
  }

  .icon.filter.has-filter {
    color: #2456d4;
  }

  .evaluation-tip {
    margin-bottom: 3px;
  }
</style>



// WEBPACK FOOTER //
// src/views/model/predict-evaluation/result-list.vue